import { useState } from 'react';
import Axios from 'axios';
import { useAppSettings } from '../hooks/useAppSettings';

interface UploadFileParams {
    category: string;
    fileName: string;
    file: File;
}

export const useUploadFile = () => {
    const { settings: appSettings } = useAppSettings();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const uploadFile = async ({ category, fileName, file }: UploadFileParams): Promise<void> => {
        if (!appSettings?.api) {
            throw new Error('API settings not available');
        }

        setIsUploading(true);
        setUploadError(null);
        setUploadSuccess(false);

        try {
            const fileBytes = await file.arrayBuffer();

            // Use Axios so the MSAL interceptor acquires a fresh token automatically
            await Axios.post(
                `${appSettings.api}UploadFile?category=${encodeURIComponent(category)}&fileName=${encodeURIComponent(fileName)}`,
                fileBytes,
                { headers: { 'Content-Type': 'application/octet-stream' } }
            );

            setUploadSuccess(true);
        } catch (error: any) {
            const message = error.response?.statusText || error.message || 'Failed to upload file';
            setUploadError(message);
            throw new Error(`Upload failed: ${message}`);
        } finally {
            setIsUploading(false);
        }
    };

    const clearMessages = () => {
        setUploadError(null);
        setUploadSuccess(false);
    };

    return {
        uploadFile,
        isUploading,
        uploadError,
        uploadSuccess,
        clearMessages,
    };
};
